/**
 * Utilidades para manejo de errores de API.
 */
import { marcarErrorMostrado } from '@/lib/erroresMostrados'

/**
 * Extrae errores por campo de una respuesta de error de la API Zen.
 * Formato esperado: { error: { details: { field: ["mensaje", ...] } } }
 */
export function extractFieldErrors(e: unknown): Record<string, string> | null {
  const err = e as { response?: { data?: { error?: { details?: Record<string, string[]> } } } }
  const details = err.response?.data?.error?.details
  if (!details || typeof details !== 'object') return null
  const fieldErrors: Record<string, string> = {}
  for (const [field, messages] of Object.entries(details)) {
    if (Array.isArray(messages) && messages.length > 0) fieldErrors[field] = messages[0]
  }
  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null
}

export interface ErrorDeCampo {
  /** Ruta con puntos e índices desde 0: `lines.1.unit_price`, `customer`. */
  ruta: string
  mensaje: string
}

/**
 * Todos los errores de `details`, incluidos los anidados de las listas.
 *
 * DRF devuelve el error de una línea como `{lines: [{}, {unit_price: ['…']}]}`:
 * `extractFieldErrors` toma `messages[0]`, que ahí es el `{}` de la línea sana,
 * y el mensaje real se pierde. Pasó el 2026-10-19 con un pedido en zenlatam que
 * mostraba «corrige los errores marcados» sin nada marcado.
 */
export function aplanarErroresDeCampo(e: unknown): ErrorDeCampo[] {
  const err = e as { response?: { data?: { error?: { details?: unknown } } } }
  const salida: ErrorDeCampo[] = []
  const recorrer = (valor: unknown, ruta: string) => {
    if (typeof valor === 'string') {
      salida.push({ ruta, mensaje: valor })
    } else if (Array.isArray(valor)) {
      const todosTexto = valor.every(v => typeof v === 'string')
      valor.forEach((v, i) => recorrer(v, todosTexto ? ruta : ruta ? `${ruta}.${i}` : String(i)))
    } else if (valor && typeof valor === 'object') {
      for (const [clave, v] of Object.entries(valor)) recorrer(v, ruta ? `${ruta}.${clave}` : clave)
    }
  }
  recorrer(err.response?.data?.error?.details, '')
  return salida
}

/**
 * Los errores de react-hook-form (`{customer: {message}, collection_contacts: [, {email: {message}}]}`)
 * en el mismo formato, para listarlos junto a los del servidor.
 */
export function aplanarErroresDelFormulario(errores: unknown, ruta = ''): ErrorDeCampo[] {
  if (!errores || typeof errores !== 'object') return []
  const { message } = errores as { message?: unknown }
  if (typeof message === 'string' && message) return [{ ruta, mensaje: message }]
  return Object.entries(errores as Record<string, unknown>)
    .filter(([clave]) => clave !== 'ref' && clave !== 'type' && clave !== 'types')
    .flatMap(([clave, v]) => aplanarErroresDelFormulario(v, ruta ? `${ruta}.${clave}` : clave))
}

/**
 * La ruta en palabras: `lines.1.unit_price` → «Línea 2 · Precio unitario».
 * `listas` nombra cada elemento de una lista; `campos`, cada campo.
 */
export function rutaLegible(
  ruta: string,
  campos: Record<string, string>,
  listas: Record<string, string> = {},
): string {
  const partes = ruta.split('.').filter(p => p !== 'non_field_errors')
  const salida: string[] = []
  for (let i = 0; i < partes.length; i++) {
    const parte = partes[i]
    const siguiente = partes[i + 1]
    if (listas[parte] && siguiente !== undefined && /^\d+$/.test(siguiente)) {
      salida.push(`${listas[parte]} ${Number(siguiente) + 1}`)
      i++
    } else if (!/^\d+$/.test(parte)) {
      salida.push(campos[parte] ?? parte)
    }
  }
  return salida.join(' · ')
}

/**
 * Extrae el mensaje de error general de una respuesta de error de la API Zen.
 * Formato esperado: { error: { message: "..." } }
 */
export function extractErrorMessage(e: unknown, fallback: string): string {
  // Extraer el mensaje para mostrarlo cuenta como «ya se le avisó al usuario»:
  // la red de seguridad global (mutationErrorHandler) no duplica el aviso.
  marcarErrorMostrado(e)
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? fallback
}
